import { BASE_AUDIT_API, authorizedFetch, getHttpErrorMessage } from '@/services/api';
import type { AuditConfBO, BankConfBO } from '@/services/auditService/interface/afs-from';

export interface FileDownloadResult {
    blob: Blob;
    filename?: string;
    contentType?: string;
}

const base = `${BASE_AUDIT_API}/afs-form`;

function tryParseFilenameFromContentDisposition(contentDisposition: string | null): string | undefined {
    if (!contentDisposition) return undefined;

    const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
    const raw = match?.[1] || match?.[2];
    if (!raw) return undefined;

    try {
        return decodeURIComponent(raw.replace(/\+/g, '%20'));
    } catch {
        return raw.replace(/\+/g, ' ');
    }
}

async function fetchFile(path: string, options?: RequestInit): Promise<FileDownloadResult> {
    // Must use authorizedFetch so path is ciphered when payload cipher is on.
    // Binary Excel responses stay unencrypted (CryptoPayloadFilter only wraps JSON).
    const res = await authorizedFetch(path, options);

    if (!res.ok) {
        throw new Error(await getHttpErrorMessage(res, 'File download error'));
    }

    // Gateway crypto errors often return HTTP 200 + JSON Result (e.g. code 4605).
    const contentType = res.headers.get('content-type') || undefined;
    if ((contentType || '').toLowerCase().includes('json')) {
        throw new Error(await getHttpErrorMessage(res, 'File download error'));
    }

    const filename = tryParseFilenameFromContentDisposition(res.headers.get('content-disposition'));
    const blob = await res.blob();

    return { blob, filename, contentType };
}

/**
 * CLOSED: 单文件 AFS Form 下载已合并进 Zip。
 * 请使用 downloadFsPreviewZip（GET /fs-preview-download/download-zip）。
 * GET /afs-form/exportAll 后端已注释关闭。
 */
// export async function exportAfsForm(companyId: number, annualId: number) {
//     const params = new URLSearchParams({
//         companyId: String(companyId),
//         annualId: String(annualId),
//     });
//
//     return fetchFile(`${base}/exportAll?${params.toString()}`, {
//         method: 'GET',
//     });
// }

export async function exportExcelBConf(companyId: number, annualId: number) {
    const params = new URLSearchParams({
        companyId: String(companyId),
        annualId: String(annualId),
    });

    return fetchFile(`${base}/export-bankConf?${params.toString()}`, {
        method: 'POST',
    });
}

export async function exportExcelBConfForm(bankConfBO: BankConfBO) {
    return fetchFile(`${base}/export-bankConf-form`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(bankConfBO),
    });
}

export async function exportExcelAuditConfForm(auditConfBO: AuditConfBO) {
    return fetchFile(`${base}/export-auditConf`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(auditConfBO),
    });
}

/** JX1 directors Audit Confirmation (audit_conf_template.xlsx) */
export async function exportExcelAuditConfDirForm(auditConfBO: AuditConfBO) {
    return fetchFile(`${base}/export-auditConf-dir`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(auditConfBO),
    });
}

export const auditAfsFromService = {
    // CLOSED → Zip: exportAfsForm
    exportExcelBConf,
    exportExcelBConfForm,
    exportExcelAuditConfForm,
    exportExcelAuditConfDirForm,
};
